import { Alert, Button, Group, Modal, Stack, Text } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useState } from 'react';
import {
  apiErrorText,
  type PortfolioService,
  useUpdateCycle,
} from '@/models/clientCompanies';

export function CycleEdit({ service }: { service: PortfolioService }) {
  const [open, setOpen] = useState(false);
  const cycle = service.currentCycle;
  if (!cycle) return null;
  return (
    <>
      <Button size="compact-xs" variant="subtle" onClick={() => setOpen(true)}>
        Edit current cycle
      </Button>
      {open && (
        <CycleEditModal
          service={service}
          onClose={() => setOpen(false)}
          initial={{
            periodStart: cycle.periodStart?.slice(0, 10) ?? null,
            renewalDate: cycle.renewalDate?.slice(0, 10) ?? null,
          }}
        />
      )}
    </>
  );
}

function CycleEditModal({
  service,
  initial,
  onClose,
}: {
  service: PortfolioService;
  initial: { periodStart: string | null; renewalDate: string | null };
  onClose: () => void;
}) {
  const update = useUpdateCycle();
  const [periodStart, setPeriodStart] = useState(initial.periodStart);
  const [renewalDate, setRenewalDate] = useState(initial.renewalDate);
  const [error, setError] = useState<string | null>(null);
  const changed =
    (periodStart !== initial.periodStart && !!periodStart) ||
    (renewalDate !== initial.renewalDate && !!renewalDate);

  const save = async () => {
    setError(null);
    try {
      await update.mutateAsync({
        serviceId: Number(service.id),
        ...(periodStart && periodStart !== initial.periodStart
          ? { periodStart }
          : {}),
        ...(renewalDate && renewalDate !== initial.renewalDate
          ? { renewalDate }
          : {}),
      });
      onClose();
    } catch (e) {
      setError(apiErrorText(e));
    }
  };

  return (
    <Modal opened onClose={onClose} title="Edit current renewal cycle">
      <Stack gap="sm">
        <Text fw={500}>{service.title}</Text>
        <DateInput
          label="Cycle start"
          valueFormat="DD.MM.YYYY"
          value={periodStart}
          onChange={setPeriodStart}
        />
        <DateInput
          label="Next renewal"
          valueFormat="DD.MM.YYYY"
          value={renewalDate}
          onChange={setRenewalDate}
        />
        <Text fz="xs" c="dimmed">
          Renewals are not processed in this stage: the dates are kept for the
          renewal process.
        </Text>
        {error && (
          <Alert color="red" variant="light" p="xs">
            <Text fz="xs">{error}</Text>
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={!changed} loading={update.isPending}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
